import { useHistory, useLocation } from "react-router";
import React, { useMemo, useState, useCallback } from "react";
import { Box, SxProps, Theme, Typography, useTheme } from "@mui/material";
import AlertModel from "../components/investorOverview/common/AlertModel";
import { RoleStackItem } from "../redux-store/types/router";
import { useSelector } from "react-redux";
import { RootStateType } from "../redux-store/reducers";
import { USER_ROLES } from "../utils/constant";

interface CrumbItem {
    name: string;
    path: string;
    warning?: boolean;
    state?: any;
    search?: string;
    textSx?: SxProps<Theme>;
    isTextLowerCase?: boolean;
    handleClick?: () => void;
}

interface InitialProps {
    array: CrumbItem[];
    dynamic?: boolean;
}

const BreadCrum = (props: InitialProps) => {
    const { array } = props;
    const { role = "" } = useSelector((store: RootStateType) => store.auth);
    const { palette, typography } = useTheme();
    const history = useHistory();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [replacePath, setReplacePath] = useState({
        path: '',
        state: {} as any,
        search: ''
    });

    // ALWAYS check for roleStack FIRST, spread if present, otherwise use array
    const finalArray = useMemo((): CrumbItem[] => {

        // Check if investor login then return array directly
        if (role === USER_ROLES.INVESTOR) {
            return array;
        }
        // Get role stack from router state
        const roleStack: RoleStackItem[] = (location.state as { roleStack?: RoleStackItem[] })?.roleStack || [];


        // Start with role stack crumbs (if any)
        const crumbs: CrumbItem[] = [{ name: 'Dashboard', path: '/dashboard' }];

        // SPREAD ROLE STACK FIRST
        if (roleStack.length > 0) {
            roleStack.forEach((stackItem, index) => {
                crumbs.push({
                    name: stackItem.name,
                    path: '/dashboard',
                    state: { roleStack: roleStack.slice(0, index + 1) }
                });
            });
        }

        // THEN APPEND ARRAY BREADCRUMBS
        array.forEach((arrayItem) => {
            // Skip Dashboard if already added from stack
            if (`${arrayItem?.name ?? ""}`.toLowerCase() !== 'dashboard') {
                crumbs.push(arrayItem);
            }
        });

        return crumbs.length === 1 ? [] : crumbs;
    }, [location.state, array]);

    const handleRouter = useCallback(async (item: CrumbItem, index: number) => {
        // Check if we're in stack mode (current crumb has roleStack state)
        const isStackMode = !!item.state?.roleStack;

        if (isStackMode && index > 0) {
            const roleStack: RoleStackItem[] = item.state.roleStack;
            const truncatedStack = roleStack.slice(0, index);

            if (item.handleClick && !item.warning) {
                await item.handleClick();
            }

            if (item.warning) {
                setOpen(true);
                setReplacePath({
                    path: item.path,
                    state: { roleStack: truncatedStack },
                    search: ''
                });
            } else {
                setOpen(false);
                history.replace({
                    pathname: item.path,
                    state: { roleStack: truncatedStack }
                });
            }
            return;
        }

        // Original static logic
        if (item?.handleClick && !item.warning) {
            await item.handleClick();
        }

        if (item.warning) {
            setOpen(true);
            setReplacePath((prev: any) => ({
                path: item.path,
                state: item?.state || {},
                search: item?.search || ""
            }));
        } else {
            setOpen(false);
            history.replace({
                pathname: item.path,
                state: item?.state || {},
                search: item?.search || ""
            });
        }
    }, [history]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleConfirm = useCallback(() => {
        const dummy = [...finalArray].filter((item) => item.path === replacePath.path)[0];
        dummy.handleClick?.();

        setOpen(false);
        if (replacePath.state?.roleStack) {
            history.replace({
                pathname: replacePath.path,
                state: replacePath.state
            });
        } else {
            history.replace({
                pathname: replacePath.path,
                state: replacePath?.state || {},
                search: replacePath?.search || ""
            });
        }
    }, [finalArray, history, replacePath]);

    return (
        <>
            <Box sx={{
                display: { xs: "none", sm: "flex" },
                flexDirection: 'row',
                alignItems: 'center',
                gap: '5px',
                flexWrap: 'wrap'
            }}>
                {(finalArray ?? []).map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1px' }}>
                        <Box
                            component="button"
                            onClick={() => index === finalArray.length - 1 ? null : handleRouter(item, index)}
                            sx={{
                                border: 'none',
                                background: 'transparent',
                                fontFamily: typography.fontFamily,
                                color: index === finalArray.length - 1 ? palette.text.secondary : palette.primary[500],
                                fontSize: 14,
                                fontWeight: 500,
                                textWrap: 'nowrap',
                                cursor: index === finalArray.length - 1 ? "not-allowed" : 'pointer',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                maxWidth: {
                                    xs: index === finalArray.length - 1 ? 'fit-content' : '100px',
                                    sm: '150px',
                                    md: 'fit-content',
                                    lg: 'fit-content'
                                },
                                width: '100%',
                                textTransform: 'capitalize',
                                ...item.textSx,
                            }}
                        >
                            {((item ?? "")?.isTextLowerCase ?? true) ? `${(item?.name ?? "").toLowerCase()}` : item.name}
                        </Box>
                        <Typography color={index === finalArray.length - 1 ? palette.text.secondary : palette.primary[500]}>
                            {`${index === finalArray.length - 1 ? '' : '/'}`}
                        </Typography>
                    </Box>
                ))}
            </Box>
            {open && (
                <AlertModel
                    handleClose={handleClose}
                    handleConfirm={handleConfirm}
                    confirmBtnText="Go Back Anyway"
                    open={open}
                    setOpen={setOpen}
                    title="Data not saved, will be lost!"
                    desc="Going back will result in loss of your entered data. Are you sure you want to proceed?"
                />
            )}
        </>
    );
};

export default BreadCrum;
